import { orderList } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";
import { RootState } from "@/store";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import WMSAvatar from "@/styles/WMSAvatar";
import { CommonListParams } from "@/types/common";
import { ChangeEnum, ConfirmedEnum } from "@/types/inventory";
import { OrderV2, QCStateEnum } from "@/types/inventoryV2";
import { dateFormat } from "@/utils/format";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import { DatePicker, Flex, Input, Select, TableProps, Tabs, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import dayjs, { Dayjs } from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RowSelectionType } from "antd/es/table/interface";
import useSourceType from "@/hooks/useSourceType";
import useIqcEnums from "@/pages/Warehouse/pages/IQCList/hooks/useIqcEnums";
const { RangePicker } = DatePicker;

export enum TabEnum {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}
interface SelectOrderContentProps {
  selectedIds?: string[];
  selectType?: RowSelectionType;
  onlyTab?: TabEnum;
  onSelect?: (ids: string[]) => void;
  onSelectOrder?: (orders: OrderV2[]) => void;
  initOrders?: OrderV2[];
  disIds?: string[];
  defaultConfirm?: ConfirmedEnum;
};
const SelectOrderContent: FC<SelectOrderContentProps> = ({ selectedIds, selectType = "radio", onlyTab, onSelect, onSelectOrder, initOrders, disIds = [], defaultConfirm }) => {
  const { t } = useTranslation();
  const { getQCState } = useIqcEnums();
  const { changeTypes, getChangeType, getOpType } = useSourceType();
  const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg);
  const [list, total, loading, { fetchList }] = useDataList<OrderV2, [CommonListParams, string, ConfirmedEnum?]>({ fetchApi: orderList })
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [keyword, setKeyword] = useState<string>("");
  const [orderType, setOrderType] = useState<ChangeEnum | undefined>();
  const [startTime, setStartTime] = useState<Dayjs | null>();
  const [endTime, setEndTime] = useState<Dayjs | null>();
  const [activeTab, setActiveTab] = useState<TabEnum>(onlyTab || TabEnum.INBOUND);
  const [confirmed, setConfirmed] = useState<ConfirmedEnum | undefined>(defaultConfirm);

  // 当组件挂载或依赖项变化时，重新获取数据
  useEffect(() => {
    if (curOrg && !!!initOrders) {
      fetch(0);
    }
    return () => abortControllerRef.current?.abort();
  }, [activeTab, startTime, endTime, orderType, confirmed, initOrders]);


  const fetch = (offset: number) => {
    if (!curOrg) return console.warn('curOrg is not available');
    abortControllerRef.current?.abort();

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    let types: ChangeEnum[];
    if (orderType !== undefined) {
      types = [orderType];
    } else {
      types = activeTab === TabEnum.INBOUND
        ? [ChangeEnum.PRE_IN, ChangeEnum.IN]  // 所有入库类型
        : [ChangeEnum.PRE_OUT, ChangeEnum.OUT];  // 所有出库类型
    }

    offset === 0 && tableRef?.current?.resetTablePage();
    fetchList({
      body: keyword,
      ints: types,  // 传递多个类型
      pagination: { offset, take },
      startTime: startTime ? startTime.startOf('day').valueOf() : undefined,
      endTime: endTime ? endTime.endOf('day').valueOf() : undefined,
      controller, // ✅ 传递 controller 用于取消请求
    }, curOrg.id, confirmed)
  };
  const handleTabChange = (key: string) => {
    setActiveTab(key as TabEnum);
    setOrderType(undefined);
    setStartTime(null);
    setEndTime(null);
  };
  const tableTabs = [
    {
      key: TabEnum.INBOUND,
      label: t('warehouse.inbound'),
    },
    {
      key: TabEnum.OUTBOUND,
      label: t('warehouse.outbound'),
    },
  ];
  const filteredChangeTypes = useMemo(() => {
    if (activeTab === TabEnum.INBOUND) {
      // 入库类型
      return changeTypes.filter(item =>
        [ChangeEnum.ORDER_IN, ChangeEnum.PRE_IN, ChangeEnum.IN].includes(item.value)
      );
    } else {
      // 出库类型
      return changeTypes.filter(item =>
        [ChangeEnum.PRE_OUT, ChangeEnum.OUT, ChangeEnum.SALES_OUT].includes(item.value)
      );
    }
  }, [activeTab]);

  const headerChildren = (
    <Flex justify="space-between" style={{ width: "100%" }} align="center">
      {!!!onlyTab && <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tableTabs}
        type="card"
      />}
      <Flex gap={10}>
        <RangePicker
          value={[startTime, endTime]}
          onChange={val => {
            if (val) {
              setStartTime(val[0])
              setEndTime(val[1])
            } else {
              setStartTime(null)
              setEndTime(null)
            }
          }}
          placeholder={[t('order.startTime'), t('order.endTime')]}
          style={{ width: 280 }}
        />
        <Select style={{ minWidth: 160 }} value={confirmed} placeholder={t('warehouse.plconfed')}
          options={[
            { label: t('warehouse.noconf'), value: ConfirmedEnum.NOTCONFIRM },
            { label: t('warehouse.confed'), value: ConfirmedEnum.CONFIRM },
          ]}
          onChange={val => setConfirmed(val)} allowClear disabled={defaultConfirm !== undefined}
        />
        <Select
          allowClear
          placeholder={t('order.selectChangeTypePl')}
          value={orderType}
          onChange={(e) => {
            setOrderType(e ? e as ChangeEnum : undefined);
          }}
          options={filteredChangeTypes}
          style={{ width: 160 }}
        />
        <Input.Search
          style={{ width: 200 }}
          value={keyword}
          enterButton
          onChange={(e) => setKeyword(e.target.value.trim())}
          onSearch={(e) => {
            fetch(0);
            tableRef?.current?.resetTablePage();
          }}
          placeholder={t('order.searchByOrderNumber')}
        />
      </Flex>
    </Flex>
  );
  // 根据 tab 动态生成表格列
  const columns = useMemo((): TableProps<OrderV2>["columns"] => [
    {
      title: activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
      dataIndex: "number",
      key: "number",
      width: 200,
      fixed: "left",
      ellipsis: true,
      sorter: (a, b) => {
        const valA = a?.number ?? "";
        const valB = b?.number ?? "";
        return valA.localeCompare(valB);
      },
      render(value, record, index) {
        return <Typography.Link >{record.number}</Typography.Link>
      },
    },
    {
      title: t('order.preparedBy'),
      dataIndex: "creator",
      key: "creator",
      width: 140,
      render: (_, record) => (
        record.creator && <Flex align="center">
          <WMSAvatar size={30} creator={record.creator} uniChar={record.creator.id} avatarId={record.creator.avatar} alt={getUserAvatarTip(record.creator)} />
          <SText text={getUserName(record.creator)} style={{ width: 70 }} />
        </Flex>
      ),
    },
    {
      title: t('order.preparationTime'),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 100,
      render: (_, record) => record.createdAt && dateFormat(dayjs(record.createdAt)),
    },
    {
      title: t('warehouse.requiredArrivalAt'),
      dataIndex: "preTime",
      key: "preTime",
      width: 100,
      render: (preTime) => preTime && dateFormat(dayjs(preTime)),
    },
    {
      title: t('warehouse.actualArrivalAt'),
      dataIndex: "arrivalAt",
      key: "arrivalAt",
      width: 100,
      render: (arrivalAt) => arrivalAt && dateFormat(dayjs(arrivalAt)),
    },
    {
      title: t('order.confirmedTime'),
      dataIndex: "confirmedAt",
      key: "confirmedAt",
      width: 100,
      render: (confirmedAt) => confirmedAt && dateFormat(dayjs(confirmedAt)),
    },
    {
      title: t('order.oprtType'),
      key: "type",
      dataIndex: "type",
      width: 120,
      fixed: "right",
      render: (_, { type, id }) => {
        let info = getChangeType(type);
        return (
          <SText text={info?.label ?? ""} tagMode color={info?.color} />
        );
      },
    },
    {
      title: t('order.operationSource'),
      key: "opType",
      dataIndex: "opType",
      width: 90,
      fixed: "right",
      render: (_, { opType, id }) => {
        let info = getOpType(opType);
        return (
          <SText text={info?.label ?? ""} tagMode color={info?.color} />
        );
      },
    },
    {
      title: t('order.operationStatus'),
      key: "confirmState",
      dataIndex: "confirmState",
      width: 90,
      fixed: "right",
      render: (_, { confirmState, id }) => {
        // 根据tab显示不同的状态文字
        if (activeTab === TabEnum.INBOUND) {
          // 入库：0-在途，1-已入库
          const label = confirmState === 0 ? t('order.inTransit') : t('order.inboundCompleted');
          const color = confirmState === 0 ? 'orange' : 'green';
          return <SText text={label ?? ""} tagMode color={color} />
        } else {
          // 出库：0-备料，1-已出库
          const label = confirmState === 0 ? t('order.preparing') : t('order.outboundCompleted');
          const color = confirmState === 0 ? 'orange' : 'green';
          return <SText text={label ?? ""} tagMode color={color} />
        }
      },
    },
    {
      title: t('warehouse.iqcState'),
      key: "iqcState",
      width: 100,
      fixed: "right",
      dataIndex: "iqcState",
      hidden: activeTab === TabEnum.OUTBOUND,
      render: (_, record) => {
        let label = getQCState(record.iqcState);
        return (
          label && <SText tagMode text={label.label} color={label.color} />
        )
      }
    },

  ], [activeTab, t]);

  const rowSelection: TableProps<AnyObject>['rowSelection'] = {
    type: selectType,
    selectedRowKeys: selectedIds || [],
    onChange: (selectedRowKeys: React.Key[], selectedRows: AnyObject[]) => {
      onSelect?.(selectedRowKeys as string[]);
      onSelectOrder?.(selectedRows as OrderV2[])
    },
    getCheckboxProps: record => ({
      // disabled: (record as OrderV2).iqcState === QCStateEnum.QC_PASS || (record as OrderV2).iqcState === QCStateEnum.QC_FAIL
      disabled: disIds.includes(record.id)
    })
  };
  return (
    <STableLayout
      columns={columns as Array<AnyObject>}
      dataSource={initOrders || list}
      loading={loading}
      headerChildren={!!initOrders ? null : headerChildren}
      total={total}
      rowSelection={rowSelection}
      rowKey={"id"}
      handleChange={fetch}
    />
  )
};

export default SelectOrderContent;